import dayjs from "dayjs";
import { useMemo } from "react";
import { toast } from "react-toastify";
import ViewReservationSummaryModel from "src/models/ViewReservationSummaryModel";
import { ReservationType } from "src/utils/common";

export const generateFilters = (startDate, endDate, agent) => {
    if (startDate && !endDate) {
        throw new Error("You should select an end date also.");
    }

    if (!startDate && endDate) {
        throw new Error("You should select a start date also.");
    }

    const filters = [];

    if (startDate && endDate) {
        const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
        const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
        filters.push(`CreatedDate ge ${formattedStartDate} and CreatedDate le ${formattedEndDate}`);
    }

    if (agent) {
        filters.push(`AgentId eq '${agent.value}'`);
    }

    return filters.join(" and ");
};

export const handleSearch = async ({
    startDate,
    endDate,
    agent,
    dispatch,
    fetchBackOfficeReservationSummaryList,
    instance,
    accounts,
    setLoading
}) => {
    setLoading(true);
    try {
        const filter = generateFilters(startDate, endDate, agent);
        await dispatch(
            fetchBackOfficeReservationSummaryList(instance, accounts, {
                filter,
                top: 100,
                orderBy: "CreatedDate desc",
            })
        );
    } catch (error) {
        toast.error(error.message || "An error occurred.");
    } finally {
        setLoading(false);
    }
};

export const transformToSummaryModels = (results) => {
    return results.map(i => ({ ...new ViewReservationSummaryModel(i) }))
}

export const useReservationViewSelectionCostData = (reservationOverview) => {
    return useMemo(() => {
        if (reservationOverview?.Hotels?.length > 0) {
            const hotel = reservationOverview.Hotels[0];
            const sellData = hotel.Rooms.map((room) => ({
                Type: ReservationType.hotel,
                Name: room.RoomName,
                RoomType: room.RoomType,
                UnitCount: room.UnitCount,
                Rate: room.RoomRates[0]?.ClientNetSell?.Total || 0,
                Total: room.RoomRates[0]?.ClientNetSell?.Total || 0,
            }));

            const buyData = hotel.Rooms.map((room) => ({
                Type: ReservationType.hotel,
                Name: room.RoomName,
                RoomType: room.RoomType,
                UnitCount: room.UnitCount,
                Rate: room.RoomRates[0]?.ContractBuy?.Total || 0,
                Total: room.RoomRates[0]?.ContractBuy?.Total || 0,
            }));

            return { sellData, buyData };
        }

        if (reservationOverview?.Services?.length > 0) {
            const service = reservationOverview.Services[0];
            const sellData = service.Units.map((unit) => ({
                Type: ReservationType.service,
                Name: unit.ServiceUnitName,
                ServiceType: unit.ServiceType,
                UnitCount: unit.UnitCount,
                Rate: unit.ServiceUnitRates[0]?.ClientNetSell?.Total || 0,
                Total: unit.ServiceUnitRates[0]?.ClientNetSell?.Total || 0,
            }));

            const buyData = service.Units.map((unit) => ({
                Type: ReservationType.service,
                Name: unit.ServiceUnitName,
                ServiceType: unit.ServiceType,
                UnitCount: unit.UnitCount,
                Rate: unit.ServiceUnitRates[0]?.ContractBuy?.Total || 0,
                Total: unit.ServiceUnitRates[0]?.ContractBuy?.Total || 0,
            }));

            return { sellData, buyData };
        }

        return { sellData: [], buyData: [] };
    }, [reservationOverview]);
};
